/**
 * useMatrixData Hook
 * Fetches matrix data from Supabase RPC and manages state
 *
 * Supports:
 * - Single project view (projectId)
 * - Stack view (multiple projectIds)
 * - v3 RPC (with client-side enrichment)
 * - v4 RPC (with server-side enrichment - recommended)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { enrichAssignments } from '@/lib/qualificationMatcher';
import { useMatrixBroadcast } from './useMatrixBroadcast';

// Stable empty references to prevent infinite loops
const EMPTY_ARRAY = [];
const EMPTY_MAP = new Map();

/**
 * Custom hook to fetch and process matrix data
 *
 * @param {string|null} projectId - Single project ID (for single space view)
 * @param {Object} options - Configuration options
 * @param {string[]} options.projectIds - Array of project IDs (for stack view)
 * @param {boolean} options.isStackView - Whether this is a stack view
 * @param {string} options.version - RPC version to use: 'v3' or 'v4' (default: 'v4')
 * @returns {Object} { loading, error, data, assignmentMap, requirementStatsMap, refetch }
 */
export function useMatrixData(projectId, options = {}) {
  const {
    projectIds = null,
    projects = null, // [{id, name}] for stack view - enables requirement dedup with space names
    isStackView = false,
    version = 'v4'
  } = options;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rawData, setRawData] = useState(null);

  // Determine which RPC function to use
  const rpcName = version === 'v4' ? 'get_matrix_data_v4' : 'get_matrix_data_v3';

  // Fetch data function
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      if (isStackView && projectIds && projectIds.length > 0) {
        // Stack view: fetch data from all projects in parallel
        const results = await Promise.all(
          projectIds.map(pid => supabase.rpc(rpcName, { p_project_id: pid }))
        );

        // Merge results from all projects
        const merged = mergeStackData(results, projectIds, version, projects);
        setRawData(merged);
      } else if (projectId) {
        // Single project view
        const { data, error: fetchError } = await supabase.rpc(rpcName, {
          p_project_id: projectId
        });

        if (fetchError) throw fetchError;
        setRawData(data);
      }
    } catch (err) {
      setError(err);
      console.error('[useMatrixData] Error fetching matrix data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, projectIds, projects, isStackView, rpcName, version]);

  // Set up real-time broadcast subscription
  const handleBroadcast = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useMatrixBroadcast(projectId, handleBroadcast);

  // Initial data load
  useEffect(() => {
    if (projectId || (isStackView && projectIds?.length)) {
      fetchData();
    }
  }, [fetchData, projectId, projectIds, isStackView]);

  // Process and enrich data based on version
  const processedData = useMemo(() => {
    if (!rawData) return null;

    if (version === 'v4') {
      // v4 returns pre-enriched data from the database
      // Normalize snake_case fields to camelCase for client compatibility
      const normalizedAssignments = (rawData.assignments || []).map(a => ({
        ...a,
        // Normalize to camelCase for client code compatibility
        effectiveStatus: a.effective_status,
        hasQualification: a.has_qualification,
        qualifiedFromOtherSpace: a.qualified_from_other_space,
        isExipiringSoon: a.is_expiring_soon,
        renewalStatus: a.renewal_status,
        assignmentEvidenceUrl: a.assignment_evidence_url,
        assignmentCertificateNumber: a.assignment_certificate_number,
        isAssigned: a.assigned === true,
        evidenceFiles: a.evidence_files || a.qualification?.evidence_files || []
      }));

      // Create a Set of existing assignment keys to avoid duplicates
      const existingKeys = new Set(
        normalizedAssignments.map(a => `${a.team_member_id}-${a.requirement_id}`)
      );

      // Convert cross-space qualifications to virtual assignments
      // These are team members who have qualifications from other spaces
      const virtualAssignments = (rawData.crossSpaceQualifications || [])
        .filter(csq => !existingKeys.has(`${csq.team_member_id}-${csq.requirement_id}`))
        .map(csq => ({
          team_member_id: csq.team_member_id,
          requirement_id: csq.requirement_id,
          assigned: false,
          qualifiedFromOtherSpace: true,
          effectiveStatus: csq.effective_status,
          hasQualification: true,
          isAssigned: false,
          qualification: csq.qualification,
          _isVirtual: true
        }));

      // Merge actual assignments with virtual cross-space assignments
      const allAssignments = [...normalizedAssignments, ...virtualAssignments];

      return {
        requirements: rawData.requirements || [],
        teamMembers: rawData.teamMembers || [],
        assignments: allAssignments,
        crossSpaceQualifications: rawData.crossSpaceQualifications || [],
        summary: rawData.summary || { teamSize: 0, requirementCount: 0 }
      };
    }

    // v3: requires client-side enrichment
    const requirements = rawData.requirements || [];
    const teamMembers = rawData.teamMembers || [];
    const assignments = rawData.assignments || [];
    const qualifications = rawData.qualifications || [];

    const enrichedAssignments = enrichAssignments(assignments, requirements, qualifications);

    return {
      requirements,
      teamMembers,
      assignments: enrichedAssignments,
      qualifications, // Include qualifications for cross-space lookup
      crossSpaceQualifications: [],
      summary: { teamSize: teamMembers.length, requirementCount: requirements.length }
    };
  }, [rawData, version]);

  // Build assignment lookup map for O(1) cell access
  const assignmentMap = useMemo(() => {
    if (!processedData) return EMPTY_MAP;

    const map = new Map();

    // Add all assignments
    processedData.assignments.forEach(assignment => {
      const key = `${assignment.team_member_id}-${assignment.requirement_id}`;
      map.set(key, assignment);
    });

    // Add cross-space qualifications as virtual assignments (v4 only)
    if (version === 'v4' && processedData.crossSpaceQualifications) {
      processedData.crossSpaceQualifications.forEach(csq => {
        const key = `${csq.team_member_id}-${csq.requirement_id}`;
        // Only add if no existing assignment
        if (!map.has(key)) {
          map.set(key, {
            team_member_id: csq.team_member_id,
            requirement_id: csq.requirement_id,
            assigned: false,
            // Normalized camelCase fields
            qualifiedFromOtherSpace: true,
            effectiveStatus: csq.effective_status,
            hasQualification: true,
            isAssigned: false,
            qualification: csq.qualification,
            // Virtual assignment marker
            _isVirtual: true
          });
        }
      });
    }

    return map;
  }, [processedData, version]);

  // Build requirement stats map from pre-calculated stats (v4 only)
  const requirementStatsMap = useMemo(() => {
    if (!processedData) return EMPTY_MAP;

    const map = new Map();

    processedData.requirements.forEach(req => {
      if (version === 'v4' && req.stats) {
        // v4: use pre-calculated stats
        const stats = req.stats;
        const totalValid = (stats.valid || 0) + (stats.validCrossSpace || 0);
        const totalExpiring = (stats.expiring || 0) + (stats.expiringCrossSpace || 0);
        const required = req.required || processedData.summary.teamSize;

        map.set(req.id, {
          ...stats,
          required,
          compliancePercentage: required > 0
            ? Math.min(100, Math.round(((totalValid + totalExpiring) / required) * 100))
            : 0
        });
      } else {
        // v3: stats will need to be calculated by consumer
        map.set(req.id, {
          valid: 0,
          validCrossSpace: 0,
          expiring: 0,
          expiringCrossSpace: 0,
          expired: 0,
          missing: 0,
          booked: 0,
          quoting: 0,
          totalAssigned: 0,
          required: processedData.summary.teamSize,
          compliancePercentage: 0
        });
      }
    });

    return map;
  }, [processedData, version]);

  return {
    loading,
    error,
    data: processedData,
    requirements: processedData?.requirements || EMPTY_ARRAY,
    teamMembers: processedData?.teamMembers || EMPTY_ARRAY,
    qualifications: processedData?.qualifications || EMPTY_ARRAY,
    assignmentMap: assignmentMap || EMPTY_MAP,
    requirementStatsMap: requirementStatsMap || EMPTY_MAP,
    summary: processedData?.summary || null,
    refetch: fetchData
  };
}

/**
 * Get a dedup key for a requirement.
 * Uses name as the key since course_link_id is often per-space, not global.
 */
function getRequirementDedupKey(req) {
  return (req.name || '').trim().toLowerCase();
}

/**
 * Merge data from multiple projects for stack view.
 * Deduplicates requirements across spaces by course_link or name,
 * tracking which spaces each requirement belongs to.
 *
 * @param {Array} results - Array of RPC results per project
 * @param {string[]} projectIds - Array of project IDs
 * @param {string} version - RPC version ('v3' or 'v4')
 * @param {Array|null} projects - Optional [{id, name}] for space name lookup
 */
function mergeStackData(results, projectIds, version, projects = null) {
  const merged = {
    requirements: [],
    teamMembers: [],
    assignments: [],
    crossSpaceQualifications: [],
    summary: {
      teamSize: 0,
      requirementCount: 0,
      totalAssignments: 0
    }
  };

  // Build project name lookup
  const projectNameMap = new Map();
  if (projects) {
    projects.forEach(p => projectNameMap.set(p.id, p.name));
  }

  // Track unique members across projects (by user_id)
  const seenMembers = new Set();

  // Requirement dedup: dedupKey -> canonical requirement object
  const requirementsByKey = new Map();
  // Map original requirement_id -> canonical requirement_id (for remapping assignments)
  const requirementIdRemap = new Map();

  results.forEach((result, index) => {
    if (result.error || !result.data) {
      console.error(`[useMatrixData] Error fetching project ${projectIds[index]}:`, result.error);
      return;
    }

    const data = result.data;
    const pid = projectIds[index];
    const spaceName = projectNameMap.get(pid) || pid;

    // Deduplicate requirements across spaces
    (data.requirements || []).forEach(req => {
      const dedupKey = getRequirementDedupKey(req);
      const existing = requirementsByKey.get(dedupKey);

      if (existing) {
        // Requirement already seen from another space - add this space
        existing.spaces.push({ id: pid, name: spaceName });
        // Map this requirement's ID to the canonical one
        requirementIdRemap.set(req.id, existing.id);
      } else {
        // First time seeing this requirement - it becomes the canonical one
        const canonical = {
          ...req,
          project_id: pid,
          spaces: [{ id: pid, name: spaceName }]
        };
        requirementsByKey.set(dedupKey, canonical);
        requirementIdRemap.set(req.id, req.id); // maps to itself
      }
    });

    // Add team members (dedupe by user_id)
    (data.teamMembers || []).forEach(member => {
      if (!seenMembers.has(member.user_id)) {
        seenMembers.add(member.user_id);
        merged.teamMembers.push({ ...member, project_id: pid });
      }
    });

    // Add assignments, remapping requirement_id to canonical
    (data.assignments || []).forEach(assignment => {
      const canonicalReqId = requirementIdRemap.get(assignment.requirement_id) || assignment.requirement_id;
      merged.assignments.push({
        ...assignment,
        requirement_id: canonicalReqId,
        _original_requirement_id: assignment.requirement_id,
        project_id: pid
      });
    });

    // Add cross-space qualifications (v4 only)
    if (version === 'v4' && data.crossSpaceQualifications) {
      (data.crossSpaceQualifications || []).forEach(csq => {
        const canonicalReqId = requirementIdRemap.get(csq.requirement_id) || csq.requirement_id;
        merged.crossSpaceQualifications.push({
          ...csq,
          requirement_id: canonicalReqId,
          _original_requirement_id: csq.requirement_id,
          project_id: pid
        });
      });
    }

    // Aggregate summary
    if (data.summary) {
      merged.summary.totalAssignments += data.summary.totalAssignments || 0;
    }
  });

  // Build final requirements array from deduped map
  merged.requirements = Array.from(requirementsByKey.values());
  merged.summary.requirementCount = merged.requirements.length;

  // Team size is unique members
  merged.summary.teamSize = seenMembers.size;

  return merged;
}

// Export for backwards compatibility
export default useMatrixData;
